import api from './api.service';
import type { RejectionPayload } from './rejection.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface SupplierInvoiceLineDTO {
  id: string;
  supplier_invoice_id: string;
  grn_item_id?: string;
  po_item_id?: string;
  po_overhead_id?: string | null;
  line_number: number;
  item_code?: string;
  item_description?: string;
  supplier_description?: string | null;
  /** Technical specification snapshot (from GRN / service receipt / PO line). */
  specifications?: string | null;
  /** Enriched from item master on read. */
  profile_grade?: string | null;
  profile_heat_no?: string | null;
  profile_dimension?: string | null;
  profile_length?: string | null;
  quantity_billed: number;
  unit_price: number;
  amount: number;
  tax_amount?: number;
  cost_category?: string | null;
  project_id?: string;
  warehouse_id?: string;
  goodsReceiptNoteItem?: { id: string; grn_id?: string; goodsReceiptNote?: { id?: string; grn_number?: string } };
  goodsReceiptNote?: { id?: string; grn_number?: string };
  purchaseOrderItem?: { id: string };
  purchaseOrderOverhead?: {
    id: string;
    overhead_type?: string;
    description?: string | null;
    amount?: number;
    invoiced_amount?: number;
  };
}

export interface PoOverheadOnInvoiceDTO {
  id: string;
  line_number?: number;
  overhead_type: string;
  description?: string | null;
  remarks?: string | null;
  amount?: number;
  original_amount?: number;
  invoiced_amount?: number;
  currency?: string;
}

export interface UnclearedPoOverhead {
  id: string;
  po_id: string;
  po_number: string | null;
  line_number: number;
  overhead_type: string;
  description: string | null;
  amount: number;
  invoiced_amount: number;
  remaining_amount: number;
  currency: string;
}

export interface SupplierInvoiceDTO {
  id: string;
  company_id: string;
  invoice_number: string;
  supplier_id: string;
  supplier_name_snapshot?: string | null;
  supplier_invoice_number: string;
  supplier_invoice_date: string;
  po_id?: string;
  grn_id?: string;
  department_id?: string | null;
  received_date: string;
  due_date: string;
  posting_date?: string;
  payment_terms?: string;
  subtotal: number;
  invoice_type?: string;
  discount_amount: number;
  discount_account_id?: string | null;
  discountAccount?: { id: string; account_code: string; account_name: string } | null;
  total_after_discount: number;
  vat_amount: number;
  freight_charges: number;
  other_charges: number;
  grand_total: number;
  /** Sum of overhead lines cleared onto this bill. */
  overhead_amount?: number;
  /** Linked PO overhead rows (for view/print even before clearing). */
  po_overheads?: PoOverheadOnInvoiceDTO[];
  po_overhead_total?: number;
  currency: string;
  debit_account_id?: string;
  paid_amount: number;
  balance_due: number;
  debit_note_adjusted_amount?: number;
  advance_adjusted_amount?: number;
  matching_status: string;
  variance_amount?: number;
  variance_notes?: string | null;
  invoice_status: string;
  approval_status: string;
  approval_instance_id?: string | null;
  gl_posted: boolean;
  journal_voucher_id?: string;
  notes?: string;
  created_at?: string;
  /** Set when supplier submitted details via public portal link */
  portal_submitted_at?: string | null;
  supplier?: { id: string; supplier_name?: string; supplier_code?: string };
  purchaseOrder?: { id: string; po_number?: string };
  goodsReceiptNote?: { id: string; grn_number?: string };
  debitAccount?: { id: string; account_code?: string; account_name?: string };
  lines?: SupplierInvoiceLineDTO[];
}

export interface OpenBillableLineDTO {
  grn_item_id: string;
  grn_id: string;
  grn_number: string;
  grn_date: string;
  po_id: string;
  po_number: string;
  po_item_id: string;
  line_number: number;
  item_code: string | null;
  item_description: string | null;
  supplier_description?: string | null;
  specifications?: string | null;
  accepted_quantity: number;
  billed_quantity: number;
  pending_quantity: number;
  unit_price: number;
  pending_value: number;
  project_id: string | null;
  project_code?: string | null;
  project_number?: string | null;
  project_name?: string | null;
  warehouse_id: string | null;
  uom: string | null;
  /** From PO item – tax is computed from these, no manual tax entry */
  vat_applicable: boolean;
  vat_percentage: number;
  /** Inherited from PO item for cost-control display */
  wbs_node_id?: string | null;
  cbs_code_id?: string | null;
  cost_category?: string | null;
}

export interface OpenBillableServiceReceiptDTO {
  service_receipt_id: string;
  sr_number: string;
  sr_date: string;
  po_id: string;
  po_number: string;
  po_milestone_id: string;
  milestone_label: string | null;
  supplier_id: string;
  approved_value: number;
  billed_value: number;
  pending_value: number;
  retention_amount: number;
  currency: string;
  project_id: string | null;
  department_id: string | null;
  vat_applicable: boolean;
  vat_percentage: number;
  /** Estimated VAT embedded in pending_value (VAT-inclusive). */
  pending_vat: number;
  lines: Array<{
    service_receipt_line_id: string;
    line_number: number;
    description: string | null;
    line_value: number;
    billed_value: number;
    pending_value: number;
    project_id: string | null;
  }>;
}

export interface SupplierInvoiceFilters {
  supplier_id?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  bill_number?: string;
  po_id?: string;
  grn_id?: string;
  service_receipt_id?: string;
  invoice_type?: string;
}

export interface CreateSupplierInvoiceDTO {
  supplier_id: string;
  supplier_invoice_number: string;
  supplier_invoice_date: string;
  received_date: string;
  due_date: string;
  payment_terms?: string;
  debit_account_id?: string;
  retention_account_id?: string;
  department_id?: string;
  notes?: string;
  po_milestone_id?: string;
  service_receipt_id?: string;
  invoice_type?: string;
  is_retention_release?: boolean;
  amount?: number;
  lines?: Array<{
    grn_item_id?: string;
    service_receipt_line_id?: string;
    quantity_billed?: number;
    amount?: number;
    unit_price?: number;
    description?: string;
    project_id?: string;
  }>;
  overhead_lines?: Array<{
    po_overhead_id: string;
    amount: number;
  }>;
  discount_amount?: number;
  discount_account_id?: string | null;
  vat_amount?: number;
  freight_charges?: number;
  other_charges?: number;
}

export interface UpdateSupplierInvoiceDTO {
  supplier_invoice_number?: string;
  supplier_invoice_date?: string;
  received_date?: string;
  due_date?: string;
  payment_terms?: string;
  debit_account_id?: string;
  department_id?: string;
  notes?: string;
  lines?: Array<{
    id?: string;
    grn_item_id: string;
    quantity_billed: number;
    unit_price?: number;
  }>;
  overhead_lines?: Array<{
    po_overhead_id: string;
    amount: number;
  }>;
  discount_amount?: number;
  discount_account_id?: string | null;
  vat_amount?: number;
  freight_charges?: number;
  other_charges?: number;
}

export const supplierInvoiceService = {
  list(filters?: SupplierInvoiceFilters): Promise<SupplierInvoiceDTO[]> {
    const q = new URLSearchParams();
    if (filters?.supplier_id) q.set('supplier_id', filters.supplier_id);
    if (filters?.status) q.set('status', filters.status);
    if (filters?.date_from) q.set('date_from', filters.date_from);
    if (filters?.date_to) q.set('date_to', filters.date_to);
    if (filters?.bill_number) q.set('bill_number', filters.bill_number);
    if (filters?.po_id) q.set('po_id', filters.po_id);
    if (filters?.grn_id) q.set('grn_id', filters.grn_id);
    if (filters?.service_receipt_id) q.set('service_receipt_id', filters.service_receipt_id);
    if (filters?.invoice_type) q.set('invoice_type', filters.invoice_type);
    return api.get(`/procurement/supplier-invoices?${q.toString()}`).then((res) => unwrap<SupplierInvoiceDTO[]>(res) ?? []);
  },

  getOpenLines(supplierId: string): Promise<OpenBillableLineDTO[]> {
    return api
      .get(`/procurement/supplier-invoices/open-lines?supplier_id=${encodeURIComponent(supplierId)}`)
      .then((res) => unwrap<OpenBillableLineDTO[]>(res) ?? []);
  },

  getOpenBillableServiceReceipts(supplierId: string): Promise<OpenBillableServiceReceiptDTO[]> {
    return api
      .get(
        `/procurement/supplier-invoices/open-billable-service-receipts?supplier_id=${encodeURIComponent(supplierId)}`
      )
      .then((res) => unwrap<OpenBillableServiceReceiptDTO[]>(res) ?? []);
  },

  getUnclearedOverheads(params: {
    po_id?: string;
    grn_ids?: string[];
    supplier_id?: string;
  }): Promise<UnclearedPoOverhead[]> {
    const q = new URLSearchParams();
    if (params.po_id) q.set('po_id', params.po_id);
    if (params.grn_ids?.length) q.set('grn_ids', params.grn_ids.join(','));
    if (params.supplier_id) q.set('supplier_id', params.supplier_id);
    return api
      .get(`/procurement/supplier-invoices/uncleared-overheads?${q.toString()}`)
      .then((res) => unwrap<UnclearedPoOverhead[]>(res) ?? []);
  },

  getById(id: string): Promise<SupplierInvoiceDTO | null> {
    return api.get(`/procurement/supplier-invoices/${id}`).then((res) => unwrap<SupplierInvoiceDTO>(res) ?? null);
  },

  create(dto: CreateSupplierInvoiceDTO): Promise<SupplierInvoiceDTO> {
    return api.post('/procurement/supplier-invoices', dto).then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  checkDuplicate(
    supplierId: string,
    supplierInvoiceNumber: string,
    excludeId?: string
  ): Promise<{ duplicate: boolean; existing_id?: string; existing_bill_number?: string; is_deleted?: boolean }> {
    return api
      .get('/procurement/supplier-invoices/check-duplicate', {
        params: {
          supplier_id: supplierId,
          supplier_invoice_number: supplierInvoiceNumber,
          ...(excludeId ? { exclude_id: excludeId } : {}),
        },
      })
      .then((res) => unwrap(res));
  },

  update(id: string, dto: UpdateSupplierInvoiceDTO): Promise<SupplierInvoiceDTO> {
    return api.put(`/procurement/supplier-invoices/${id}`, dto).then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  submit(id: string): Promise<SupplierInvoiceDTO> {
    return api.post(`/procurement/supplier-invoices/${id}/submit`, {}).then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  approve(id: string, remarks?: string): Promise<SupplierInvoiceDTO> {
    return api
      .post(`/procurement/supplier-invoices/${id}/approve`, { remarks: remarks ?? undefined })
      .then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  rejectApproval(id: string, payload: RejectionPayload): Promise<SupplierInvoiceDTO> {
    return api
      .post(`/procurement/supplier-invoices/${id}/reject-approval`, payload)
      .then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  revokeApproval(id: string, reason: string): Promise<SupplierInvoiceDTO> {
    return api
      .post(`/procurement/supplier-invoices/${id}/revoke-approval`, { reason })
      .then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  alignTotal(id: string): Promise<SupplierInvoiceDTO> {
    return api.post(`/procurement/supplier-invoices/${id}/align-total`, {}).then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  post(id: string, opts: { discount_account_id?: string } = {}): Promise<SupplierInvoiceDTO> {
    return api.post(`/procurement/supplier-invoices/${id}/post`, opts).then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  unpost(id: string): Promise<SupplierInvoiceDTO> {
    return api.post(`/procurement/supplier-invoices/${id}/unpost`, {}).then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  cancel(id: string): Promise<SupplierInvoiceDTO> {
    return api.post(`/procurement/supplier-invoices/${id}/cancel`, {}).then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  issuePortalToken(id: string): Promise<{ token: string }> {
    return api.post(`/procurement/supplier-invoices/${id}/portal-token`, {}).then((res) => unwrap<{ token: string }>(res));
  },

  getVoucherBySupplierInvoiceId(supplierInvoiceId: string): Promise<any | null> {
    return api
      .get('/finance/vouchers/by-reference', {
        params: { reference_type: 'SupplierInvoice', reference_id: supplierInvoiceId },
      })
      .then((res) => unwrap(res) ?? null)
      .catch(() => null);
  },

  getOpenDebitNotes(supplierId: string): Promise<OpenDebitNoteRow[]> {
    return api
      .get(`/procurement/supplier-invoices/open-debit-notes?supplier_id=${encodeURIComponent(supplierId)}`)
      .then((res) => unwrap<OpenDebitNoteRow[]>(res) ?? []);
  },

  applyDebitNotes(invoiceId: string, allocations: ApplyDebitNoteAllocationDTO[]): Promise<ApplyDebitNotesResult> {
    return api
      .post(`/procurement/supplier-invoices/${invoiceId}/apply-debit-notes`, { allocations })
      .then((res) => unwrap<ApplyDebitNotesResult>(res));
  },

  getDebitNoteAllocations(invoiceId: string): Promise<DebitNoteAllocationRow[]> {
    return api
      .get(`/procurement/supplier-invoices/${invoiceId}/debit-note-allocations`)
      .then((res) => unwrap<DebitNoteAllocationRow[]>(res) ?? []);
  },

  getOpenAdvances(supplierId: string, poId?: string): Promise<OpenAdvanceRow[]> {
    const q = new URLSearchParams({ supplier_id: supplierId });
    if (poId) q.set('po_id', poId);
    return api
      .get(`/procurement/supplier-invoices/open-advances?${q.toString()}`)
      .then((res) => unwrap<OpenAdvanceRow[]>(res) ?? []);
  },

  applyAdvances(invoiceId: string, allocations: ApplyAdvanceAllocationDTO[]): Promise<ApplyAdvancesResult> {
    return api
      .post(`/procurement/supplier-invoices/${invoiceId}/apply-advances`, { allocations })
      .then((res) => unwrap<ApplyAdvancesResult>(res));
  },

  getPlannedAdvances(invoiceId: string): Promise<PlannedAdvanceRow[]> {
    return api
      .get(`/procurement/supplier-invoices/${invoiceId}/planned-advances`)
      .then((res) => unwrap<PlannedAdvanceRow[]>(res) ?? []);
  },

  setPlannedAdvances(invoiceId: string, allocations: ApplyAdvanceAllocationDTO[]): Promise<PlannedAdvanceRow[]> {
    return api
      .put(`/procurement/supplier-invoices/${invoiceId}/planned-advances`, { allocations })
      .then((res) => unwrap<PlannedAdvanceRow[]>(res) ?? []);
  },

  getAdvanceAllocations(invoiceId: string): Promise<AdvanceAllocationRow[]> {
    return api
      .get(`/procurement/supplier-invoices/${invoiceId}/advance-allocations`)
      .then((res) => unwrap<AdvanceAllocationRow[]>(res) ?? []);
  },

  getMatchingQueue(params?: { matching_status?: string; search?: string }): Promise<SupplierInvoiceDTO[]> {
    return api
      .get('/procurement/supplier-invoices/matching-queue', { params })
      .then((res) => unwrap<SupplierInvoiceDTO[]>(res) ?? []);
  },

  getMatchResult(id: string): Promise<ThreeWayMatchResultDTO> {
    return api
      .get(`/procurement/supplier-invoices/${id}/match-result`)
      .then((res) => unwrap<ThreeWayMatchResultDTO>(res));
  },

  runMatch(id: string): Promise<ThreeWayMatchResultDTO> {
    return api.post(`/procurement/supplier-invoices/${id}/match`, {}).then((res) => unwrap<ThreeWayMatchResultDTO>(res));
  },

  forceMatch(id: string, notes?: string): Promise<ThreeWayMatchResultDTO> {
    return api
      .post(`/procurement/supplier-invoices/${id}/force-match`, { notes })
      .then((res) => unwrap<ThreeWayMatchResultDTO>(res));
  },

  holdMatch(id: string, notes?: string): Promise<SupplierInvoiceDTO> {
    return api
      .post(`/procurement/supplier-invoices/${id}/hold`, { notes })
      .then((res) => unwrap<SupplierInvoiceDTO>(res));
  },

  releaseMatchHold(id: string): Promise<ThreeWayMatchResultDTO> {
    return api
      .post(`/procurement/supplier-invoices/${id}/release-hold`, {})
      .then((res) => unwrap<ThreeWayMatchResultDTO>(res));
  },
};

export interface ThreeWayLineMatchDTO {
  line_id: string;
  line_number: number;
  item_code: string | null;
  invoice_qty: number;
  invoice_unit_price: number;
  invoice_amount: number;
  grn_accepted_qty: number | null;
  grn_status: string | null;
  po_unit_price: number | null;
  expected_unit_price: number | null;
  expected_amount: number | null;
  qty_delta: number | null;
  price_delta: number | null;
  amount_delta: number;
  passed: boolean;
  issues: string[];
}

export interface ThreeWayMatchResultDTO {
  invoice_id: string;
  invoice_number: string;
  invoice_type: string;
  matching_status: string;
  variance_amount: number;
  variance_notes: string | null;
  qty_tolerance_pct: number;
  price_tolerance_pct: number;
  lines: ThreeWayLineMatchDTO[];
}

export interface OpenDebitNoteRow {
  id: string;
  debit_note_number: string;
  debit_note_date: string;
  total_amount: number;
  adjusted_amount: number;
  open_balance: number;
  debit_note_type: string;
  purchase_return_id: string | null;
  remarks: string | null;
}

export interface ApplyDebitNoteAllocationDTO {
  supplier_debit_note_id: string;
  amount: number;
}

export interface ApplyDebitNotesResult {
  invoice: SupplierInvoiceDTO;
  allocations: DebitNoteAllocationRow[];
}

export interface DebitNoteAllocationRow {
  id: string;
  allocated_amount: number;
  adjustment_date: string;
  supplierDebitNote?: { id: string; debit_note_number?: string; debit_note_date?: string; total_amount?: number };
}

export interface OpenAdvanceRow {
  id: string;
  voucher_number: string;
  voucher_date: string;
  amount: number;
  adjusted_amount: number;
  open_balance: number;
  narration: string | null;
  payment_mode: string;
}

export interface ApplyAdvanceAllocationDTO {
  payment_voucher_id: string;
  amount: number;
}

export interface PlannedAdvanceRow {
  payment_voucher_id: string;
  voucher_number: string;
  voucher_date: string;
  planned_amount: number;
  open_balance: number;
}

export interface ApplyAdvancesResult {
  invoice: SupplierInvoiceDTO;
  allocations: AdvanceAllocationRow[];
}

export interface AdvanceAllocationRow {
  id: string;
  allocated_amount: number;
  adjustment_date: string;
  paymentVoucher?: { id: string; voucher_number?: string; voucher_date?: string; amount?: number };
}
